"use client";

import { Logo } from "@scott/ui-d2c";
import type {
  Category,
  CustomerNavItem,
  NavItem,
  SubCategory,
} from "@scott/ui-d2c/types";
import { useEffect, useState } from "react";
import { useConfig } from "@/providers/config-provider";
import { ArrowNav } from "./arrow-nav";
import { FirstPanel } from "./first-panel";
import { FourthPanel } from "./fourth-panel";
import { navigationStore } from "./navigation-data-layer-store";
import { SecondPanel } from "./second-panel";
import { ThirdPanel } from "./third-panel";
import { TopNavigation } from "./top-navigation";
import { useNavigationStore } from "./use-navigation-store";

interface NavigationContainerProps {
  navItems: NavItem[];
  customerNavItems: CustomerNavItem[];
}

const NavigationContainer = ({
  navItems,
  customerNavItems,
}: NavigationContainerProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const { brand } = useConfig();
  const [openPanels, setOpenPanels] = useState<string[]>([]);
  const [secondPanelData, setSecondPanelData] = useState<NavItem | null>(null);
  const [thirdPanelData, setThirdPanelData] = useState<Category | null>(null);
  const [fourthPanelData, setFourthPanelData] = useState<SubCategory | null>(
    null
  );
  const { clearAll: clearNavigationState } = useNavigationStore();
  // console.log(state);

  useEffect(() => {
    if (isVisible) {
      // Prevent background from scrolling
      document.body.style.overflow = "hidden";
    } else {
      // Re-enable background scrolling
      document.body.style.overflow = "";
    }

    // Cleanup function to reset overflow when component unmounts
    return () => {
      document.body.style.overflow = "";
    };
  }, [isVisible]);

  // Update local state when component mounts
  // biome-ignore lint/correctness/useExhaustiveDependencies: suppress dependency warning
  useEffect(() => {
    clearNavigationState(); // Clear the store on mount
  }, []);

  // Handler for changing panels
  const handlePanelChange = (
    panel: string,
    data: NavItem | Category | SubCategory
  ) => {
    setIsVisible(true);
    setOpenPanels((prev) => {
      setTimeout(() => {
        const panelElement = document.getElementById(panel);
        const linksContainer = panelElement?.querySelector(".linkscontainer");
        if (linksContainer) {
          linksContainer.scrollTop = 0;
        }
      }, 10);

      const newPanels = [...prev];
      if (!newPanels.includes(panel)) {
        newPanels.push(panel);
        // Reset scroll position to top for the newly opened panel
        // Delay the scroll reset
      }
      // Close fourth panel when opening second or third
      if (panel === "second" || panel === "third") {
        return newPanels.filter((p) => p !== "fourth");
      }
      return newPanels;
    });

    // Set data for the appropriate panel
    if (panel === "second") {
      // @ts-expect-error not today
      setSecondPanelData(data);
      setFourthPanelData(null); // Reset fourth panel data
    } else if (panel === "third") {
      // @ts-expect-error not today
      setThirdPanelData(data);
      setFourthPanelData(null); // Reset fourth panel data
    } else if (panel === "fourth") {
      // @ts-expect-error not today
      setFourthPanelData(data);
    }
  };

  // Handler for hamburger menu click
  const handleHamburgerClick = () => {
    setIsVisible(true);
    setOpenPanels(["first"]);
  };

  // Handler for closing the navigation
  const handleCloseNavClick = () => {
    setIsVisible(false);
    setOpenPanels([]);
    navigationStore.clearAll();
  };

  // Handler for back button click
  const handleBackClick = () => {
    setOpenPanels((prev) => {
      const newPanels = [...prev];
      newPanels.pop();
      return newPanels;
    });
  };

  return (
    <>
      {navItems && (
        <TopNavigation
          brand={brand}
          navItems={navItems}
          onHamburgerClick={handleHamburgerClick}
          onPanelChange={handlePanelChange}
        />
      )}
      {isVisible && (
        <div
          aria-hidden="true"
          className="fixed inset-0 z-10 bg-black opacity-50"
          onClick={handleCloseNavClick}
        />
      )}
      <div
        className={`position-fixed height-80 fixed top-10 left-0 z-50 flex align-center transition-transform ease-in-out ${
          isVisible ? "translate-x-20" : "-translate-x-60"
        }`}
        id="logo-container"
      >
        <Logo brand={brand} isSide={true} variant="default" />
      </div>
      <div className="navigation-container flex overflow-hidden">
        <ArrowNav
          isVisible={isVisible}
          onBack={handleBackClick}
          onClose={handleCloseNavClick}
          showBackButton={openPanels.length > 1}
        />
        {navItems && (
          <FirstPanel
            customerNavItems={customerNavItems}
            isActive={isVisible && openPanels.includes("first")}
            navItems={navItems}
            onPanelChange={handlePanelChange}
          />
        )}
        <SecondPanel
          data={secondPanelData}
          isActive={isVisible && openPanels.includes("second")}
          onPanelChange={handlePanelChange}
        />
        <ThirdPanel
          data={thirdPanelData}
          isActive={isVisible && openPanels.includes("third")}
          onPanelChange={handlePanelChange}
        />
        <FourthPanel
          data={fourthPanelData}
          isActive={isVisible && openPanels.includes("fourth")}
        />
      </div>
    </>
  );
};

NavigationContainer.displayName = "NavigationContainer";

export { NavigationContainer };
